/* ==========================================================================
   Clarté — JavaScript natif, sans dépendance
   ========================================================================== */
(() => {
  const config = window.themeConfig || { routes: {}, strings: {} };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Utilitaires ---------- */
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  const fetchJSON = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(options.headers || {}) }
    });
    const data = await response.json();
    if (!response.ok || data.status) throw data;
    return data;
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------- Panneaux (menu mobile, recherche, panier) ---------- */
  const openPanels = new Set();

  class Panel extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest(`[data-${this.closeAttr}]`)) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') this.trapFocus(event);
      });
    }

    get closeAttr() { return 'panel-close'; }

    open(trigger) {
      if (this.isOpen) return;
      this.trigger = trigger || document.activeElement;
      openPanels.forEach((panel) => panel !== this && panel.close(false));
      this.hidden = false;
      document.body.classList.add('is-locked');
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
      this.isOpen = true;
      openPanels.add(this);
      if (this.trigger && this.trigger.hasAttribute('aria-expanded')) this.trigger.setAttribute('aria-expanded', 'true');
      window.setTimeout(() => this.focusFirst(), reduceMotion ? 0 : 120);
    }

    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.isOpen = false;
      openPanels.delete(this);
      if (openPanels.size === 0) document.body.classList.remove('is-locked');
      if (this.trigger && this.trigger.hasAttribute('aria-expanded')) this.trigger.setAttribute('aria-expanded', 'false');
      window.setTimeout(() => { if (!this.isOpen) this.hidden = true; }, reduceMotion ? 0 : 380);
      if (restoreFocus && this.trigger) this.trigger.focus({ preventScroll: true });
    }

    focusFirst() {
      const target = qs('[data-autofocus]', this) || qs('input[type="search"]', this) || qs('[role="dialog"], nav[tabindex]', this);
      if (target) target.focus({ preventScroll: true });
    }

    trapFocus(event) {
      const items = qsa(focusableSelector, this).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  }

  class MobileMenu extends Panel { get closeAttr() { return 'menu-close'; } }
  customElements.define('mobile-menu', MobileMenu);

  class SearchModal extends Panel {
    get closeAttr() { return 'search-close'; }

    connectedCallback() {
      super.connectedCallback();
      this.input = qs('[data-predictive-input]', this);
      this.results = qs('[data-predictive-results]', this);
      if (!this.input || !config.routes.predictiveSearch) return;
      let timer;
      this.input.addEventListener('input', () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => this.search(this.input.value.trim()), 250);
      });
    }

    async search(term) {
      if (term.length < 2) { this.results.innerHTML = ''; return; }
      try {
        const url = `${config.routes.predictiveSearch}.json?q=${encodeURIComponent(term)}&resources[type]=product,page,article&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,vendor`;
        const data = await fetchJSON(url);
        const r = data.resources.results;
        const items = [...(r.products || []), ...(r.pages || []), ...(r.articles || [])];
        if (!items.length) {
          this.results.innerHTML = `<p class="predictive-results__empty">${config.strings.noResults}</p>`;
          return;
        }
        const escape = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
        const html = items.map((item) => {
          const image = item.featured_image && item.featured_image.url
            ? `<img src="${escape(item.featured_image.url)}${item.featured_image.url.includes('?') ? '&' : '?'}width=112" alt="" width="56" height="56" loading="lazy">`
            : (item.image ? `<img src="${escape(item.image)}" alt="" width="56" height="56" loading="lazy">` : '<span class="predictive-results__thumb"></span>');
          const price = item.price ? `<span class="predictive-results__price">${escape(this.formatPrice(item.price))}</span>` : '';
          return `<li><a href="${escape(item.url)}">${image}<span><span class="predictive-results__title">${escape(item.title)}</span><br>${price}</span></a></li>`;
        }).join('');
        const formAction = qs('form', this).getAttribute('action');
        this.results.innerHTML = `<ul role="list">${html}</ul><a class="link predictive-results__all" href="${formAction}?q=${encodeURIComponent(term)}&options[prefix]=last">Voir tous les résultats</a>`;
      } catch (error) {
        this.results.innerHTML = '';
      }
    }

    formatPrice(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return value;
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR' }).format(number);
    }
  }
  customElements.define('search-modal', SearchModal);

  /* ---------- Panier ---------- */
  class CartDrawer extends Panel {
    get closeAttr() { return 'cart-close'; }
    focusFirst() { const panel = qs('[role="dialog"]', this); if (panel) panel.focus({ preventScroll: true }); }
  }
  customElements.define('cart-drawer', CartDrawer);

  const Cart = {
    sections() {
      const ids = ['cart-drawer'];
      const page = qs('[data-cart-page]');
      if (page && page.dataset.sectionId) ids.push(page.dataset.sectionId);
      return ids;
    },

    setLoading(state) {
      const drawer = qs('#CartDrawer');
      const page = qs('[data-cart-page]');
      if (drawer) drawer.classList.toggle('is-updating', state);
      if (page) page.classList.toggle('is-updating', state);
    },

    showError(message) {
      qsa('[data-cart-error]').forEach((el) => {
        el.textContent = message || config.strings.cartError;
        el.hidden = false;
      });
    },

    render(sections, count) {
      if (!sections) return;
      const drawerHTML = sections['cart-drawer'];
      const drawer = qs('#CartDrawer');
      if (drawerHTML && drawer) {
        const fresh = qs('#CartDrawer', parseHTML(drawerHTML));
        if (fresh) {
          const currentPanel = qs('.cart-drawer__panel', drawer);
          const freshPanel = qs('.cart-drawer__panel', fresh);
          if (currentPanel && freshPanel) currentPanel.innerHTML = freshPanel.innerHTML;
        }
      }
      const page = qs('[data-cart-page]');
      if (page && sections[page.dataset.sectionId]) {
        const fresh = qs('[data-cart-page]', parseHTML(sections[page.dataset.sectionId]));
        if (fresh) page.innerHTML = fresh.innerHTML;
      }
      if (typeof count === 'number') this.updateCount(count);
    },

    updateCount(count) {
      qsa('[data-cart-count]').forEach((el) => {
        if (el.tagName === 'CART-DRAWER') { el.dataset.cartCount = count; return; }
        const label = qs('.visually-hidden', el);
        el.textContent = '';
        if (label) el.appendChild(label);
        el.appendChild(document.createTextNode(count));
        el.classList.toggle('is-empty', count === 0);
        el.classList.remove('is-bumped');
        void el.offsetWidth;
        el.classList.add('is-bumped');
      });
    },

    async add(form, button) {
      const errorEl = qs('[data-form-error]', form) || qs('[data-form-error]', form.closest('product-info') || document.createElement('div'));
      if (errorEl) errorEl.hidden = true;
      const buttons = qsa(`[data-add-button][form="${form.id}"]`).concat(qsa('[data-add-button]', form));
      buttons.forEach((b) => { b.classList.add('is-loading'); b.setAttribute('aria-disabled', 'true'); });
      try {
        const formData = new FormData(form);
        formData.append('sections', this.sections().join(','));
        formData.append('sections_url', window.location.pathname);
        const response = await fetch(config.routes.cartAdd, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
        const data = await response.json();
        if (!response.ok || data.status) throw data;
        const cart = await fetchJSON(`${config.routes.cart}.js`);
        this.render(data.sections, cart.item_count);
        const drawer = qs('#CartDrawer');
        if (drawer) drawer.open(button);
        else window.location.href = config.routes.cart;
      } catch (error) {
        const message = (error && (error.description || error.message)) || config.strings.cartError;
        if (errorEl) { errorEl.textContent = message; errorEl.hidden = false; }
        else this.showError(message);
      } finally {
        buttons.forEach((b) => { b.classList.remove('is-loading'); b.removeAttribute('aria-disabled'); });
      }
    },

    async change(line, quantity) {
      this.setLoading(true);
      try {
        const data = await fetchJSON(config.routes.cartChange, {
          method: 'POST',
          body: JSON.stringify({ line, quantity, sections: this.sections(), sections_url: window.location.pathname })
        });
        this.render(data.sections, data.item_count);
        if (data.item_count === 0 && qs('[data-cart-page]')) window.location.reload();
      } catch (error) {
        this.showError(error && (error.description || error.message));
      } finally {
        this.setLoading(false);
      }
    }
  };

  /* Soumission AJAX des formulaires produit */
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    Cart.add(form, event.submitter || qs('[data-add-button]', form));
  });

  /* Quantités (formulaire produit et panier) */
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-quantity-step]');
    if (step) {
      const wrapper = step.closest('[data-quantity]');
      const input = qs('input', wrapper);
      const min = Number(input.min || 0);
      const next = Math.max(min, Number(input.value || 0) + Number(step.dataset.quantityStep));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) { Cart.change(Number(remove.dataset.cartRemove), 0); return; }

    const cartOpen = event.target.closest('[data-cart-open]');
    const drawer = qs('#CartDrawer');
    if (cartOpen && drawer && !document.body.classList.contains('template-cart')) {
      event.preventDefault();
      drawer.open(cartOpen);
      return;
    }
    const menuOpen = event.target.closest('[data-menu-open]');
    if (menuOpen) { qs('#MobileMenu').open(menuOpen); return; }
    const searchOpen = event.target.closest('[data-search-open]');
    if (searchOpen) { qs('#SearchModal').open(searchOpen); }
  });

  let quantityTimer;
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-quantity]');
    if (!input) return;
    window.clearTimeout(quantityTimer);
    quantityTimer = window.setTimeout(() => Cart.change(Number(input.dataset.cartQuantity), Math.max(0, Number(input.value))), 350);
  });

  /* ---------- En-tête ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      const setHeight = () => document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
      setHeight();
      new ResizeObserver(setHeight).observe(this);
      const onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 8);
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* Sous-menus : fermeture au clic extérieur */
  document.addEventListener('click', (event) => {
    qsa('.site-nav__dropdown[open]').forEach((d) => { if (!d.contains(event.target)) d.removeAttribute('open'); });
    qsa('.filter[open]').forEach((d) => { if (!d.contains(event.target)) d.removeAttribute('open'); });
  });

  /* Barre de réassurance : rotation sur mobile */
  qsa('[data-rotator]').forEach((list) => {
    const items = qsa('li', list);
    if (items.length < 2 || reduceMotion) return;
    let index = 0;
    window.setInterval(() => {
      if (window.innerWidth >= 768) return;
      items[index].classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
    }, 4000);
  });

  /* ---------- Fiche produit ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = qs('[data-gallery-slides]', this);
      if (!this.track) return;
      this.slides = qsa('.gallery__slide', this.track);
      this.thumbs = qsa('[data-thumb]', this);
      this.counter = qs('[data-gallery-current]', this);
      this.lightbox = qs('[data-lightbox]', this);
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      if (this.index > 0) this.goTo(this.index, false);

      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumb))));
      const prev = qs('[data-gallery-prev]', this);
      const next = qs('[data-gallery-next]', this);
      if (prev) prev.addEventListener('click', () => this.goTo(this.index - 1));
      if (next) next.addEventListener('click', () => this.goTo(this.index + 1));

      this.addEventListener('keydown', (event) => {
        if (this.lightbox && this.lightbox.open) return;
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.goTo(this.index - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.goTo(this.index + 1); }
      });

      let scrollTimer;
      this.track.addEventListener('scroll', () => {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(() => {
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          if (i !== this.index) this.setActive(i);
        }, 80);
      }, { passive: true });

      qsa('[data-zoom]', this).forEach((button) => button.addEventListener('click', () => this.openLightbox(button)));
      if (this.lightbox) {
        qs('[data-lightbox-close]', this.lightbox).addEventListener('click', () => this.lightbox.close());
        this.lightbox.addEventListener('close', () => { if (this.zoomTrigger) this.zoomTrigger.focus({ preventScroll: true }); });
        qsa('.lightbox__item', this.lightbox).forEach((item) => item.addEventListener('click', (e) => {
          if (e.target.tagName === 'IMG') item.classList.toggle('is-zoomed');
        }));
      }
    }

    goTo(i, smooth = true) {
      const total = this.slides.length;
      const target = (i + total) % total;
      this.track.scrollTo({ left: this.track.clientWidth * target, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
      this.setActive(target);
    }

    goToMedia(mediaId) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i >= 0) this.goTo(i);
    }

    setActive(i) {
      this.index = i;
      this.slides.forEach((s, n) => s.classList.toggle('is-active', n === i));
      this.thumbs.forEach((t, n) => {
        t.classList.toggle('is-active', n === i);
        if (n === i) { t.setAttribute('aria-current', 'true'); t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' }); }
        else t.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = i + 1;
    }

    openLightbox(button) {
      if (!this.lightbox || typeof this.lightbox.showModal !== 'function') return;
      this.zoomTrigger = button;
      this.lightbox.showModal();
      const target = qs(`.lightbox__item[data-index="${button.dataset.zoom}"]`, this.lightbox);
      if (target) qs('[data-lightbox-track]', this.lightbox).scrollTo({ left: target.offsetLeft, behavior: 'auto' });
    }
  }
  customElements.define('media-gallery', MediaGallery);

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = qs('[data-product-json]', this);
      if (!json) return;
      this.data = JSON.parse(json.textContent);
      this.form = qs('[data-product-form]', this);
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-index]')) this.onOptionChange();
      });
      this.initStickyBar();
    }

    onOptionChange() {
      const selected = qsa('[data-option-index]:checked', this).sort((a, b) => a.dataset.optionIndex - b.dataset.optionIndex).map((input) => input.value);
      qsa('[data-option-value]', this).forEach((label, i) => { label.textContent = selected[i] || ''; });
      const variant = this.data.variants.find((v) => v.options.every((option, i) => option === selected[i]));
      this.updateVariant(variant);
    }

    updateVariant(variant) {
      const buttons = qsa('[data-add-button]', this).concat(qsa('[data-sticky-buy] [data-add-button]'));
      const stock = qs('[data-stock]', this);
      const stockText = qs('[data-stock-text]', this);
      if (!variant) {
        buttons.forEach((b) => { b.disabled = true; b.textContent = config.strings.unavailable; });
        if (stock) stock.classList.remove('is-available');
        if (stockText) stockText.textContent = config.strings.unavailable;
        return;
      }
      const idInput = qs('[data-variant-id]', this);
      if (idInput) idInput.value = variant.id;
      const price = qs('[data-price]', this);
      if (price) price.outerHTML = variant.price_html;
      const stickyPrice = qs('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = variant.sticky_price;
      buttons.forEach((b) => {
        b.disabled = !variant.available;
        b.textContent = variant.available ? config.strings.addToCart : config.strings.soldOut;
      });
      if (stock) stock.classList.toggle('is-available', variant.available);
      if (stockText) stockText.textContent = variant.available ? 'En stock' : 'Épuisé';
      if (variant.featured_media) {
        const gallery = qs('media-gallery', this);
        if (gallery && gallery.goToMedia) gallery.goToMedia(variant.featured_media);
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    initStickyBar() {
      const bar = qs('[data-sticky-buy]');
      const target = qs('.product-form__submit', this);
      if (!bar || !target) return;
      const footer = qs('.site-footer');
      let pastButton = false;
      let atFooter = false;
      const update = () => {
        const visible = pastButton && !atFooter;
        bar.classList.toggle('is-visible', visible);
        bar.setAttribute('aria-hidden', String(!visible));
        qsa('button', bar).forEach((b) => b.setAttribute('tabindex', visible ? '0' : '-1'));
      };
      new IntersectionObserver(([entry]) => {
        pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        update();
      }).observe(target);
      if (footer) new IntersectionObserver(([entry]) => { atFooter = entry.isIntersecting; update(); }).observe(footer);
    }
  }
  customElements.define('product-info', ProductInfo);

  /* Navigation ancrée : retire les liens orphelins et suit la section visible */
  qsa('[data-anchor-nav]').forEach((nav) => {
    const links = qsa('[data-anchor-link]', nav).filter((link) => {
      const exists = document.getElementById(decodeURIComponent(link.hash.slice(1)));
      if (!exists) link.parentElement.remove();
      return exists;
    });
    if (!links.length) { nav.closest('.shopify-section').hidden = true; return; }
    const map = new Map(links.map((l) => [document.getElementById(decodeURIComponent(l.hash.slice(1))), l]));
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        links.forEach((l) => l.classList.remove('is-active'));
        const active = map.get(entry.target);
        if (active) {
          active.classList.add('is-active');
          active.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'auto' });
        }
      });
    }, { rootMargin: '-35% 0px -60% 0px' });
    map.forEach((_, section) => observer.observe(section));
  });

  /* Recommandations produits */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      new IntersectionObserver((entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        fetch(this.dataset.url).then((r) => r.text()).then((html) => {
          const fresh = qs('product-recommendations', parseHTML(html));
          if (fresh && fresh.innerHTML.trim().length) this.innerHTML = fresh.innerHTML;
          else this.closest('.shopify-section').hidden = true;
        }).catch(() => {});
      }, { rootMargin: '0px 0px 400px 0px' }).observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Accordéons (ouverture fluide) ---------- */
  qsa('[data-accordion]').forEach((details) => {
    const summary = qs('summary', details);
    const content = qs('.accordion__content', details);
    if (!summary || !content || reduceMotion) return;
    summary.addEventListener('click', (event) => {
      event.preventDefault();
      if (details.dataset.animating) return;
      details.dataset.animating = 'true';
      if (details.open) {
        const height = content.offsetHeight;
        content.animate([{ height: `${height}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 280, easing: 'ease' }).onfinish = () => {
          details.open = false;
          delete details.dataset.animating;
        };
      } else {
        details.open = true;
        const height = content.offsetHeight;
        content.animate([{ height: '0px', opacity: 0 }, { height: `${height}px`, opacity: 1 }], { duration: 320, easing: 'ease' }).onfinish = () => {
          delete details.dataset.animating;
        };
      }
    });
  });

  /* ---------- Collection : filtres et tri ---------- */
  qsa('[data-filter-form]').forEach((form) => {
    const submit = () => {
      const params = new URLSearchParams(new FormData(form));
      for (const [key, value] of Array.from(params.entries())) if (value === '') params.delete(key);
      window.location.search = params.toString();
    };
    form.addEventListener('submit', (event) => { event.preventDefault(); submit(); });
    const sort = qs('[data-sort]', form);
    if (sort) sort.addEventListener('change', submit);
  });

  /* ---------- Apparition au défilement ---------- */
  const revealItems = qsa('[data-reveal]');
  if (document.documentElement.classList.contains('no-animations') || reduceMotion || !('IntersectionObserver' in window)) {
    revealItems.forEach((el) => el.classList.add('is-revealed'));
  } else {
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    revealItems.forEach((el) => revealObserver.observe(el));
  }

  /* Éditeur de thème : sections rechargées */
  document.addEventListener('shopify:section:load', (event) => {
    qsa('[data-reveal]', event.target).forEach((el) => el.classList.add('is-revealed'));
  });
})();
